import { useState, useRef } from "react";
import {
  NotificationContext,
  Notification as NotificationInterface,
} from "./NotificationContext";
import { Fade } from "@progress/kendo-react-animation";
import {
  Notification,
  NotificationGroup,
} from "@progress/kendo-react-notification";

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [notification, setNotification] =
    useState<NotificationInterface | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeNotification = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setNotification(null);
  };

  const notify = (notification: NotificationInterface) => {
    closeNotification();
    setNotification(notification);

    // Success/info fade after a few seconds. Errors stay up much longer and
    // warnings linger — the old 3s fade was too quick to read (especially the
    // longer validation messages). Errors are bounded rather than permanent:
    // duration 0 made the toast immortal AND it survives route changes (the
    // provider wraps the router), so an error raised on one screen sat over
    // the top of the next one until manually dismissed (MERCHIT-3257). A
    // caller can still force a specific timeout by passing an explicit
    // `duration` (duration 0 = stay until dismissed, deliberate opt-in).
    let duration = notification.duration;
    if (duration == null) {
      if (notification.type === "error") duration = 20000;
      else if (notification.type === "warning") duration = 8000;
      else duration = 3000;
    }

    if (duration > 0) {
      timeoutRef.current = setTimeout(() => {
        setNotification(null);
        timeoutRef.current = null;
      }, duration);
    }
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <NotificationGroup
        style={{
          position: "fixed",
          top: 60,
          right: "40%",
          zIndex: 100000,
          // The group floats over the page at very high z-index; without this
          // its box could swallow clicks meant for the content underneath.
          // Interactivity is re-enabled on the toast itself below.
          pointerEvents: "none",
          ...notification?.styles,
        }}
      >
        <Fade>
          {notification && (
            <Notification
              style={{ pointerEvents: "auto" }}
              type={{
                style: notification.type ?? false,
                icon: notification.showIcon ?? false,
              }}
              closable={
                notification.closable === true ||
                notification.type === "error" ||
                notification.type === "warning"
              }
              onClose={() => closeNotification()}
            >
              <div style={{ maxWidth: 520, whiteSpace: "pre-line" }}>
                {notification.message}
              </div>
            </Notification>
          )}
        </Fade>
      </NotificationGroup>
    </NotificationContext.Provider>
  );
};
